import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Collapse, ListItem, ListItemButton, ListItemText, Tooltip, alpha, lighten, useTheme } from "@mui/material";

import { useContext, useEffect, useState } from "react";

import { alertShow } from "@/components/Alert/AlertHook";
import { type Menu, SidebarContext } from "@/contexts/SidebarContext";
import { type ProductRouteKey, isUiNativeProductRoute, launchProduct } from "@/lib/platform/productHandoff";
import { User } from "@/models/user";
import { useSelector } from "@/store";
import { rootReducerName } from "@/store/rootReducer";
import { hasMenuAccess, hasVisibleMenuItem } from "@/utils/menuAccess";
import { isMenuActive } from "@/utils/menuNavigation";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { useRouter } from "next/router";

import IconRender from "./IconRender";

// Constants
const PROTECTED_SECTIONS = ["audit", "users", "groups", "roles"] as const;
const UNAUTHORIZED_MESSAGE = "You do not have access to this section. Please sign up or contact your administrator.";

// Types
interface MenuItemProps {
  menu: Menu;
  level: number;
  currentPath: string;
  itemKey?: string;
  userPermissions?: string[];
}

// Helper functions
const getMenuItemTitleTypographySx = (level: number) => {
  if (level <= 1) {
    return {
      fontSize: "1.05rem",
      fontWeight: 800,
      lineHeight: 1.2
    };
  }

  return {
    fontSize: "0.86rem",
    fontWeight: 500,
    lineHeight: 1.2
  };
};

const getMenuItemPadding = (theme: any, level: number) => {
  // Level 1 should feel like a section title.
  if (level <= 1) {
    return theme.spacing(1.6, 3);
  }
  return theme.spacing(1.0, 3);
};

const getMenuItemIconSize = (level: number) => {
  return level <= 1 ? 22 : 18;
};

const getSubmenuIndent = (theme: any, level: number) => {
  // Use a clear first-level indent, then smaller increments so deep menus retain usable width.
  if (level <= 1) return theme.spacing(2.5);
  if (level === 2) return theme.spacing(1.5);
  return theme.spacing(1);
};

const isProtectedSection = (menuTitle: string): boolean => {
  return PROTECTED_SECTIONS.includes(menuTitle.toLowerCase() as (typeof PROTECTED_SECTIONS)[number]);
};

export default function MenuItemNew({ menu, level, currentPath, itemKey, userPermissions = [] }: MenuItemProps) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const user: User = useSelector((state) => state[rootReducerName.auth].user);
  const { menuState, setMenuState } = useContext(SidebarContext);
  const router = useRouter();
  const theme = useTheme();
  const submenuSignature = menu.subMenu?.map((item) => item.path || item.title).join("|") || "";
  const accessContext = { user, userPermissions };

  const hasBlockingCapabilityFailure =
    Boolean(menu.applicationCapabilities?.length) &&
    !hasMenuAccess({ ...menu, permissions: undefined, roles: undefined }, accessContext);

  // Sync open state with current path
  useEffect(() => {
    setOpen(isMenuActive(currentPath, menu.path, menu.subMenu));
  }, [currentPath, menu.path, submenuSignature]);

  if (hasBlockingCapabilityFailure) {
    return null;
  }

  const handleClick = (e?: any) => {
    setMenuState(true);

    const isAccessDenied = isProtectedSection(menu.title) && !user;
    if (isAccessDenied) {
      alertShow({
        message: t(UNAUTHORIZED_MESSAGE),
        severity: "warning"
      });
      if (e?.preventDefault) e.preventDefault();
      return;
    }

    if (menu.subMenu?.length) {
      setOpen(!open);
    } else if (menu.path) {
      const productRouteKey = menu.productRouteKey as ProductRouteKey | undefined;
      if (productRouteKey && !isUiNativeProductRoute(productRouteKey)) {
        e?.preventDefault?.();
        void launchProduct(productRouteKey).catch((error) => {
          alertShow({
            message: error instanceof Error ? error.message : t("Unable to open the product"),
            severity: "error"
          });
        });
        return;
      }
      // Prevent redundant navigation if already on the same path.
      // Allow Next.js Link to handle the actual navigation.
      if (router.asPath === menu.path && e?.preventDefault) {
        e.preventDefault();
      }
    }
  };

  // Skip rendering if menu is hidden
  if (menu.hidden) {
    return null;
  }

  const isActive = isMenuActive(currentPath, menu.path, menu.subMenu);
  const isAccessDenied = isProtectedSection(menu.title) && !user;
  const isUnderDevelopment = menu.underDevelopment === true;
  const href = isAccessDenied ? "#" : menu.path || "";
  const accessibleTitle = t(menu.title);
  const menuLabel = isUnderDevelopment ? `${accessibleTitle} (${t("Under development")})` : accessibleTitle;

  const baseTextColor = theme.sidebar.menuIconColor;
  const textColor = level <= 1 ? baseTextColor : alpha(baseTextColor as string, 0.78);

  const boxSx = {
    width: "100%",
    display: "flex",
    justifyContent: menuState ? "start" : "center",
    alignItems: "center",
    gap: "10px",
    borderRadius: "10px",
    padding: getMenuItemPadding(theme, level),
    minHeight: level <= 1 ? 48 : 36,
    cursor: "pointer",
    ".icon": {
      color: textColor,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      "& svg": {
        fontSize: `${getMenuItemIconSize(level)}px`
      }
    },
    ".toggle_icon": {
      color: textColor
    },
    ".menu-item_title, .MuiListItemText-root": {
      color: textColor
    },
    "&.active": {
      backgroundColor: theme.sidebar.menuItemBgActive,
      color: theme.sidebar.menuItemColorActive,
      fontWeight: "bold",
      ".MuiListItemText-root": {
        color: theme.sidebar.menuItemColorActive,
        fontWeight: "700 !important"
      },
      ".icon": {
        color: theme.sidebar.menuItemColorActive
      },
      ".menu-item_title": {
        color: theme.sidebar.menuItemColorActive,
        fontWeight: "700 !important"
      },
      ".toggle_icon": {
        color: theme.sidebar.menuItemColorActive
      }
    },
    "&:hover": {
      backgroundColor: lighten(theme.sidebar.menuItemBgActive as string, 0.2),
      color: theme.sidebar.menuItemColorActive,
      fontWeight: "bold",
      ".MuiListItemText-root": {
        color: theme.sidebar.menuItemColorActive,
        fontWeight: "700 !important"
      },
      ".icon": {
        color: theme.sidebar.menuItemColorActive
      },
      ".menu-item_title": {
        color: theme.sidebar.menuItemColorActive,
        fontWeight: "700 !important"
      },
      ".toggle_icon": {
        color: theme.sidebar.menuItemColorActive
      }
    }
  };

  if (menu.subMenu?.length) {
    const visibleSubMenus = menu.subMenu.filter((subMenu) => hasVisibleMenuItem(subMenu, accessContext));
    if (!visibleSubMenus.length) return null;
    return (
      <>
        <ListItem
          key={itemKey}
          disablePadding
          sx={{
            width: "100%",
            "&.active": {
              background: "transparent !important"
            },
            ".MuiListItem-root": {
              padding: "0 !important"
            }
          }}
        >
          <Tooltip arrow placement="right" title={accessibleTitle} disableHoverListener={menuState}>
            <ListItemButton
              aria-label={accessibleTitle}
              onClick={handleClick}
              className={isActive ? "active" : ""}
              sx={{
                ...boxSx,
                "&.MuiButtonBase-root": {
                  borderRadius: "10px"
                }
              }}
            >
              <IconRender name={menu.icon} />
              {menuState && (
                <ListItemText
                  primary={accessibleTitle}
                  primaryTypographyProps={{ sx: getMenuItemTitleTypographySx(level) }}
                />
              )}
              {menuState && <div className="toggle_icon">{open ? <ExpandLess /> : <ExpandMore />}</div>}
            </ListItemButton>
          </Tooltip>
        </ListItem>

        <Collapse
          in={open}
          timeout="auto"
          unmountOnExit
          sx={{
            paddingLeft: getSubmenuIndent(theme, level),
            borderLeft: level >= 1 ? `1px solid ${alpha(theme.sidebar.menuIconColor as string, 0.18)}` : undefined
          }}
        >
          {visibleSubMenus.map((subMenu) => (
            <MenuItemNew
              key={subMenu.path || subMenu.title}
              menu={subMenu}
              level={level + 1}
              currentPath={currentPath}
              userPermissions={userPermissions}
            />
          ))}
        </Collapse>
      </>
    );
  }

  const menuButton = (
    <ListItemButton
      {...(isUnderDevelopment
        ? { component: "button", disabled: true }
        : {
            component: Link,
            id: `sidebar-menu-${menu.path?.replace(/\//g, "-").replace(/^-/, "") || "root"}`,
            href
          })}
      aria-label={menuLabel}
      aria-disabled={isUnderDevelopment || undefined}
      onClick={isUnderDevelopment ? undefined : handleClick}
      className={isActive ? "active" : ""}
      sx={{
        ...boxSx,
        "&.MuiButtonBase-root": {
          borderRadius: "10px"
        }
      }}
    >
      <IconRender name={menu.icon} />
      {menuState && (
        <ListItemText
          primary={accessibleTitle}
          secondary={isUnderDevelopment ? t("Under development") : undefined}
          primaryTypographyProps={{ sx: getMenuItemTitleTypographySx(level) }}
        />
      )}
    </ListItemButton>
  );

  return (
    <ListItem
      key={itemKey}
      disablePadding
      sx={{
        width: "100%",
        "&.active": {
          background: "transparent !important"
        },
        ".MuiListItem-root": {
          padding: "0 !important"
        }
      }}
    >
      <Tooltip arrow placement="right" title={menuLabel} disableHoverListener={menuState}>
        {isUnderDevelopment ? <span style={{ display: "block", width: "100%" }}>{menuButton}</span> : menuButton}
      </Tooltip>
    </ListItem>
  );
}
